import { ArrowRight, CheckCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Assignment, Submission } from "@/types";
import { workPackageNumbers } from "@/utils/schedule";

interface NextActionStatusProps {
  assignments: Assignment[];
  submissions: Submission[];
}

function formatDateShort(isoString: string): string {
  const d = new Date(isoString);
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short", timeZone: "Europe/London" });
  const day = d.toLocaleDateString("en-GB", { day: "numeric", timeZone: "Europe/London" });
  const month = d.toLocaleDateString("en-GB", { month: "short", timeZone: "Europe/London" });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
    hour12: false,
  });
  return `${weekday} ${day} ${month}, ${time}`;
}

function getCountdown(deadline: string) {
  const diffMs = Math.max(0, new Date(deadline).getTime() - Date.now());
  const totalSeconds = Math.floor(diffMs / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

function CountdownTile({
  value,
  label,
  accent,
}: {
  value: number;
  label: string;
  accent?: boolean;
}) {
  return (
    <div className="w-[62px] rounded-[11px] border border-white/[.14] bg-white/[.07] pt-2.5 pb-2 text-center">
      <div
        className={`text-2xl font-extrabold tabular-nums ${accent ? "text-[#7BDBAA]" : "text-[#F2EFE3]"}`}
      >
        {String(value).padStart(2, "0")}
      </div>
      <div className="mt-0.5 text-[9.5px] font-bold tracking-[.16em] text-[#F2EFE3]/55">
        {label}
      </div>
    </div>
  );
}

function FlowArrow() {
  return <ArrowRight className="w-3 h-3 opacity-50" />;
}

export function NextActionStatus({ assignments, submissions }: NextActionStatusProps) {
  const activeAssignments = assignments.filter((a) => a.time_status === "active");
  const pendingAssignment = activeAssignments.find(
    (a) => !submissions.some((s) => s.assignment_id === a.id)
  );

  const [countdown, setCountdown] = useState(() =>
    pendingAssignment ? getCountdown(pendingAssignment.deadline) : null
  );

  const deadline = pendingAssignment?.deadline;
  useEffect(() => {
    if (!deadline) return;
    setCountdown(getCountdown(deadline));
    const timer = setInterval(() => setCountdown(getCountdown(deadline)), 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  if (pendingAssignment && countdown) {
    const assignmentNum =
      workPackageNumbers(assignments).get(pendingAssignment.id) ??
      pendingAssignment.work_package_number ??
      pendingAssignment.assignment_number;
    const title = pendingAssignment.topic_title ?? `Work Package ${assignmentNum}`;

    const startMs = new Date(pendingAssignment.start_date).getTime();
    const endMs = new Date(pendingAssignment.deadline).getTime();
    const windowPct = Math.min(
      100,
      Math.max(0, ((Date.now() - startMs) / Math.max(1, endMs - startMs)) * 100)
    );

    return (
      <div className="relative overflow-hidden rounded-2xl bg-[#013220] px-[30px] pt-[26px] pb-6 text-[#F2EFE3] mb-6">
        <div
          className="absolute -top-[90px] -right-[70px] w-[340px] h-[340px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(42,182,115,.22), transparent 65%)",
          }}
        />
        <div className="relative flex flex-wrap items-center gap-[26px]">
          <div className="flex-1 min-w-[300px]">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[11px] font-bold tracking-[.16em] text-[#F2EFE3]/60">
                NEXT DEADLINE
              </span>
              <span className="whitespace-nowrap rounded-md border border-[#2AB673]/55 bg-[#2AB673]/[.14] px-[9px] py-1 text-[10px] font-bold tracking-[.1em] text-[#7BDBAA]">
                OPEN FOR SUBMISSION
              </span>
            </div>
            <h2 className="mt-2 max-w-[460px] text-[26px] font-extrabold leading-[1.25] tracking-[-.01em]">
              {title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-[9px] text-[12.5px] text-[#F2EFE3]/60">
              <span className="font-bold text-[#F2EFE3]">Submit</span>
              <FlowArrow />
              <span className="font-semibold text-[#7BDBAA]">AI feedback in ~30s</span>
              <FlowArrow />
              <span>revise</span>
              <FlowArrow />
              <span>final submission for sign-off</span>
            </div>
          </div>
          <div className="flex gap-2.5">
            <CountdownTile value={countdown.days} label="DAYS" />
            <CountdownTile value={countdown.hours} label="HRS" />
            <CountdownTile value={countdown.minutes} label="MIN" />
            <CountdownTile value={countdown.seconds} label="SEC" accent />
          </div>
          <Link
            to={`/submissions/${pendingAssignment.id}`}
            className="flex items-center gap-[9px] whitespace-nowrap rounded-[10px] bg-[#F2EFE3] px-[22px] py-3.5 text-[12.5px] font-extrabold tracking-[.1em] text-[#013220] transition-colors hover:bg-white"
          >
            VIEW BRIEF
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.2} />
          </Link>
        </div>
        <div className="relative mt-[22px]">
          <div className="flex justify-between text-[10.5px] font-bold tracking-[.12em] text-[#F2EFE3]/55">
            <span>OPENED · {formatDateShort(pendingAssignment.start_date).toUpperCase()}</span>
            <span className="hidden font-serif text-xs font-normal normal-case italic tracking-normal sm:inline">
              submit a day early to use the feedback loop
            </span>
            <span>DUE · {formatDateShort(pendingAssignment.deadline).toUpperCase()}</span>
          </div>
          <div className="relative mt-[9px] h-1.5 rounded-full bg-white/[.12]">
            <div className="h-full rounded-full bg-[#2AB673]" style={{ width: `${windowPct}%` }} />
            <div
              className="absolute top-1/2 h-[13px] w-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-[#2AB673] bg-[#F2EFE3]"
              style={{ left: `${windowPct}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  const closedAssignments = assignments.filter((a) => a.time_status === "closed");
  const allSubmitted = closedAssignments.every((a) =>
    submissions.some((s) => s.assignment_id === a.id)
  );

  if (closedAssignments.length === assignments.length && allSubmitted && assignments.length > 0) {
    return (
      <div className="flex items-center justify-center gap-3 rounded-xl border border-dashed border-brand/30 bg-brand/5 px-6 py-3.5 mb-7 text-sm text-primary font-medium">
        <CheckCircle className="w-4 h-4 text-brand" />
        <span>Programme completed!</span>
      </div>
    );
  }

  const hasActiveUnsubmitted = activeAssignments.some(
    (a) => !submissions.some((s) => s.assignment_id === a.id)
  );

  if (!hasActiveUnsubmitted && activeAssignments.length === 0) {
    const hasLockedAssignments = assignments.some((a) => a.time_status === "locked");
    if (hasLockedAssignments) {
      return (
        <div className="flex items-center justify-center gap-3 rounded-xl border border-dashed border-content-border px-6 py-3.5 mb-7 text-sm text-content-muted">
          <CheckCircle className="w-4 h-4" />
          <span>All assignments complete for this week - next week unlocks soon</span>
        </div>
      );
    }
  }

  if (activeAssignments.length > 0 && !hasActiveUnsubmitted) {
    return (
      <div className="flex items-center justify-center gap-3 rounded-xl border border-dashed border-content-border px-6 py-3.5 mb-7 text-sm text-content-muted">
        <CheckCircle className="w-4 h-4" />
        <span>All assignments complete for this week</span>
      </div>
    );
  }

  return null;
}
